import React, { useContext, useState, useEffect } from "react";
import {
  AJAX_ERROR,
  AJAX_START,
  AJAX_SUCCESS,
  ListBuilder,
  notifyError,
  notifySuccess,
  refreshJwt,
} from "@redriver/cinnamon";
import { useDispatch } from "react-redux";
import { getContent, reorderContent } from "./actions";
import { getDirectorySlug } from "modules/helpers";
import { useHistory, useLocation } from "react-router-dom";
import { RunOnChange } from "components/misc";
import { TablePagination } from "components/pagination";
import { UploadTarget } from "components/workspace";
import WorkspaceContentTable from "./WorkspaceContentTable";
import WorkspaceContentActionBar from "./WorkspaceContentActionBar";
import WorkspaceContentSelectionBar from "./WorkspaceContentSelectionBar";
import { refreshWorkspaceDirectory } from "features/System/WorkspaceMenu/actions";
import { BreadcrumbContext, SearchScopeContext } from "components/pages";
import { createFiles } from "./actions";
import { EntityType, Sort } from "constants/enums";
import UploadProgressModal from "./UploadProgressModal";
import { AJAX_PROGRESS } from "modules/axios";
import queryString from "query-string";

const WorkspaceContent = ({
  path,
  rootSource = "",
  rootBreadcrumbs = [],
  skipRoot = false,
}) => {
  const [_, setBreadcrumbs] = useContext(BreadcrumbContext);
  const [__, setSearchScope] = useContext(SearchScopeContext);
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  useEffect(() => () => setSearchScope(null), []);

  // the open document lives in the url, so previews can be linked to and survive a reload
  const previewFileId = queryString.parse(location.search).preview || null;
  const setPreviewFileId = (fileId) =>
    history.replace({
      ...location,
      search: fileId ? `?preview=${fileId}` : "",
    });

  const [tableData, setTableData] = useState([]);
  const [canReorder, setCanReorder] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [uploadingState, setUploadingState] = useState({});

  useEffect(() => setReordering(false), [path]);

  if (!Array.isArray(path) || path.length == 0) return null;

  return (
    <ListBuilder
      withTable
      loadAction={getContent}
      loadParams={path}
      initialData={{ results: [] }}
      initialFilters={{ sortName: "name", sortDirection: Sort.Asc }}
      initialPagination={{ pageSize: 25, pageNumber: 1 }}
      totalTransform={(data) => data.totalResults}
      onLoaded={(data) => {
        setSearchScope(
          data.siteId ? { siteId: data.siteId, siteName: data.siteName } : null
        );
        setTableData(
          data.results.map((x) => ({
            ...x,
            dataKey: JSON.stringify({
              type: x.directory ? EntityType.Directory : EntityType.File,
              id: x.id,
            }),
          }))
        );
        const filteredBreadcrumbs = data.breadcrumbs.filter((_, i) =>
          skipRoot ? i != 0 : true
        );

        var actualLocation =
          rootSource +
          "/" +
          path.filter((_, i) => (skipRoot ? i != 0 : true)).join("/");

        var desiredLocation =
          rootSource +
          "/" +
          filteredBreadcrumbs
            .map((x) => getDirectorySlug(x.key, x.value))
            .join("/");

        if (actualLocation != desiredLocation) {
          // keeps ?preview alive, which a bare pathname replace would drop
          history.replace({
            pathname: desiredLocation,
            search: location.search,
          });
        }
        setBreadcrumbs(
          rootBreadcrumbs.concat(
            filteredBreadcrumbs.map((x, i) => {
              if (i == filteredBreadcrumbs.length - 1) {
                return x.value;
              }
              return {
                content: x.value,
                to:
                  rootSource +
                  "/" +
                  path
                    .filter((_, i) => (skipRoot ? i != 0 : true))
                    .slice(0, i + 1)
                    .join("/"),
              };
            })
          )
        );
      }}
      renderList={(tableProps, state, baseEvents) => {
        // the nav menu lists this folder's documents too, so any content change has to re-sync it
        const events = {
          ...baseEvents,
          onRefresh: () => {
            baseEvents.onRefresh();
            dispatch(refreshWorkspaceDirectory(tableProps.data.id));
          },
        };

        const totalSelected = state.selection.allSelected
          ? state.total - (state.selection.deselected?.length || 0)
          : state.selection.selected?.length || 0;

        const selectedLength = tableData.filter(
          (x) =>
            !(state.selection.deselected || []).some((a) => a == x.dataKey) &&
            (!!state.selection.allSelected ||
              (state.selection.selected || []).some((a) => a == x.dataKey))
        ).length;

        const onChangeFilters = (filters) => {
          setCanReorder(!filters.search && !filters.sortName);
          events.onChangeFilters(filters);
        };
        return (
          <div>
            <RunOnChange
              value={state.total}
              func={() => events.onChangeSelection({})}
            />
            <WorkspaceContentActionBar
              canEdit={tableProps.data.canEdit}
              directoryId={tableProps.data.id}
              siteId={tableProps.data.siteId}
              siteRootDirectoryId={tableProps.data.siteRootDirectoryId}
              notificationFrequency={tableProps.data.notificationFrequency}
              notificationActivities={tableProps.data.notificationActivities}
              inheritedNotificationFrequency={
                tableProps.data.inheritedNotificationFrequency
              }
              inheritedNotificationActivities={
                tableProps.data.inheritedNotificationActivities
              }
              events={events}
              onRefresh={events.onRefresh}
              onChangeFilters={onChangeFilters}
              path={path}
              filters={state.filters}
              skipRoot={skipRoot}
              rootSource={rootSource}
              canReorder={canReorder}
              reordering={reordering}
              setReordering={(nextReordering) => {
                setReordering(nextReordering);
                if (!nextReordering) {
                  dispatch(
                    reorderContent(
                      {
                        ...state.pagination,
                        orderedItems: tableData.map((x) => {
                          return {
                            id: x.id,
                            type: x.directory
                              ? EntityType.Directory
                              : EntityType.File,
                          };
                        }),
                      },
                      tableProps.data.id
                    )
                  );
                }
              }}
            />

            <WorkspaceContentSelectionBar
              directoryId={tableProps.data.id}
              siteName={tableProps.data.siteName}
              siteRootDirectoryId={tableProps.data.siteRootDirectoryId}
              selection={state.selection}
              totalSelected={totalSelected}
              filters={state.filters}
              canEditContent={tableProps.data.results.some((x) => x.canEdit)}
              reordering={reordering}
              onRefresh={events.onRefresh}
              onClearSelection={() => events.onChangeSelection({})}
            />

            <UploadTarget
              onDrop={(files) => {
                const toastExpiry = 60000 * 60 * 24;
                dispatch(refreshJwt()).then((jwt) => {
                  createFiles(
                    jwt,
                    {
                      files: files.map((f) => {
                        return { file: f };
                      }),
                    },
                    tableProps.data.id,
                    true,
                    ({ state, progress, response, error }) => {
                      setUploadingState({ state, progress, response });
                      if (state == AJAX_SUCCESS) {
                        const successfulFiles =
                          files.length - response.data.length;
                        if (response.data.length) {
                          response.data.forEach((item) => {
                            dispatch(
                              notifyError(item.value, { expiry: toastExpiry })
                            );
                          });
                        }
                        if (successfulFiles > 0) {
                          dispatch(
                            notifySuccess(
                              `${
                                files.length > 1 ? successfulFiles + " f" : "F"
                              }ile${successfulFiles > 1 ? "s" : ""} uploaded`,
                              { expiry: toastExpiry }
                            )
                          );
                          events.onRefresh();
                        }
                      } else if (state == AJAX_ERROR) {
                        dispatch(notifyError(error, { expiry: toastExpiry }));
                      }
                    }
                  );
                });
              }}
              canUpload={tableProps.data.canEdit}
            >
              <WorkspaceContentTable
                tableProps={tableProps}
                state={state}
                selectedLength={selectedLength}
                onChangeFilters={onChangeFilters}
                reordering={reordering}
                tableData={tableData}
                path={path}
                skipRoot={skipRoot}
                rootSource={rootSource}
                events={events}
                setTableData={setTableData}
                previewFileId={previewFileId}
                onOpenPreview={setPreviewFileId}
                onClosePreview={() => setPreviewFileId(null)}
              />
            </UploadTarget>
            {!reordering && (
              <TablePagination
                value={state.pagination}
                onChange={events.onChangePagination}
                totalItems={state.total}
                pageSizeOptions={[10, 25, 50, 100]}
              />
            )}
            <UploadProgressModal
              open={
                uploadingState.state == AJAX_START ||
                uploadingState.state == AJAX_PROGRESS
              }
              progress={uploadingState.progress}
            />
          </div>
        );
      }}
    />
  );
};

export default WorkspaceContent;
